import { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Briefcase, Loader2, Undo2 } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSwitchToTenant } from '@/api/queries/firm';
import { cn } from '@/lib/utils';

interface FirmContextBannerProps {
  /** Height already pinned above this banner (the network banner), in px. */
  top: number;
  /** Receives this banner's rendered height, 0 when it is not shown. */
  onHeightChange: (height: number) => void;
}

/** Switches the session back to the user's home tenant; no step-up applies there. */
function BackHomeButton({ homeTenantId }: { homeTenantId: string }) {
  const switchTo = useSwitchToTenant();
  const [leaving, setLeaving] = useState(false);

  async function backHome() {
    setLeaving(true);
    try {
      await switchTo(homeTenantId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Le retour au cabinet a échoué.');
    } finally {
      setLeaving(false);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void backHome()}
      disabled={leaving}
      className={cn(
        'inline-flex flex-shrink-0 items-center gap-1.5 rounded-md bg-brand-encre px-2.5 py-1 text-xs font-semibold text-white',
        'hover:bg-brand-lagune-950 disabled:opacity-70',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-encre'
      )}
    >
      {leaving ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      Revenir au cabinet
    </button>
  );
}

/**
 * Pinned to the top of every page while the active tenant is not the
 * user's home tenant: names the client file and the firm the access comes
 * through, and switches back home in one action.
 */
export function FirmContextBanner({ top, onHeightChange }: FirmContextBannerProps) {
  const { user, tenant, switchedFirm } = useAuth();
  const ref = useRef<HTMLDivElement>(null);

  const inClientFile = !!user && !!tenant && tenant.id !== user.tenantId;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!inClientFile || !el) {
      onHeightChange(0);
      return;
    }
    const report = () => onHeightChange(el.offsetHeight);
    report();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(report);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      onHeightChange(0);
    };
  }, [inClientFile, onHeightChange]);

  if (!inClientFile || !user || !tenant) return null;

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Dossier client ouvert"
      data-testid="firm-context-banner"
      style={{ top }}
      className={cn(
        'fixed left-0 right-0 z-[60] bg-brand-turquoise text-brand-encre',
        'flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm',
        'pb-1.5 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]',
        top > 0 ? 'pt-1.5' : 'pt-[calc(0.375rem+env(safe-area-inset-top))]'
      )}
    >
      <p className="flex min-w-0 items-center gap-2 [overflow-wrap:anywhere]">
        <Briefcase className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          Dossier client : <strong className="font-semibold">{tenant.name}</strong>
          {switchedFirm ? (
            <>
              {' '}— via <strong className="font-semibold">{switchedFirm.name}</strong>
            </>
          ) : (
            ' — accès direct'
          )}
        </span>
      </p>
      <BackHomeButton homeTenantId={user.tenantId} />
    </div>
  );
}

export default FirmContextBanner;
